import { useContext } from "react";
import finacraft_api from "../api_adapters/finacraft_api";

import WelcomeStage from "./stages/WelcomeStage";
import BankAccountStage from "./stages/BankAccountStage";
import IncomeSourceStep from "./stages/IncomeSourceStage";
import SummaryStage from "./stages/SummaryStage";
import { AuthContext } from "../contexts/AuthContext";
import { OnboardingProvider, useOnboarding } from "./context/OnboardingContext";

const STORAGE_KEY = (clientId) => `onboarding_client_${clientId}`;

function OnboardingWizardContent({ clientId }) {
  const { state, setStep, bulkSaveBankAccounts, bulkSaveIncomes, clearDrafts } = useOnboarding();
  const { fetchOnboardingStatus } = useContext(AuthContext);

  const hasProgress =
    state.savedStep > 1 || state.bankAccounts.length > 0 || state.incomes.length > 0;

  const handleBankAccountComplete = async () => {
    try {
      await bulkSaveBankAccounts();
      setStep(2);
    } catch (error) {
      // Error already handled in Context
    }
  };

  const handleIncomeSourceComplete = async () => {
    try {
      await bulkSaveIncomes();
      setStep(3);
    } catch (error) {
      // Error already handled in Context
    }
  };

  const handleIncomeSourcePrevious = () => {
    setStep(1);
  };

  const handleEditBankAccounts = () => {
    setStep(1);
  };

  const handleEditIncomes = () => {
    setStep(2);
  };

  const handleOnboardingComplete = async () => {
    try {
      await finacraft_api.post(`/core_api/clients/onboarding-complete/`);
      localStorage.removeItem(STORAGE_KEY(clientId));
      await fetchOnboardingStatus?.();
    } catch (error) {
      console.error("Error completing onboarding:", error);
      alert("Error completing onboarding. Please try again.");
    }
  };

  const handleWelcomeComplete = () => {
    // Always resume to last non-welcome step (tracked in context),
    // otherwise start at the first data step.
    if (hasProgress) {
      setStep(state.resumeStep || 1);
      return;
    }
    setStep(1);
  };

  const handleStartOverConfirmed = () => {
    clearDrafts();
    setStep(1);
  };

  const steps = [
    <WelcomeStage
      key="welcome"
      onComplete={handleWelcomeComplete}
      onStartOver={handleStartOverConfirmed}
      hasProgress={hasProgress}
    />,
    <BankAccountStage
      key="bank"
      onComplete={handleBankAccountComplete}
    />,
    <IncomeSourceStep
      key="income"
      clientId={clientId}
      onComplete={handleIncomeSourceComplete}
      onPrevious={handleIncomeSourcePrevious}
    />,
    <SummaryStage
      key="summary"
      onEditBankAccounts={handleEditBankAccounts}
      onEditIncomes={handleEditIncomes}
      onCompleteOnboarding={handleOnboardingComplete}
    />,
  ];

  return <div className="container mt-4">{steps[state.currentStep]}</div>;
}

export default function OnboardingWizard({ clientId }) {
  return (
    <OnboardingProvider clientId={clientId}>
      <OnboardingWizardContent clientId={clientId} />
    </OnboardingProvider>
  );
}
